import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, ArrowRight, Clock } from "lucide-react";
import { useAnalytics } from "@/hooks/useAnalytics";
import { format, parseISO, isToday, isTomorrow, isBefore } from "date-fns";
import { useTranslation } from "react-i18next";

interface ActionRequiredSectionProps {
  userId: string | undefined;
}

export const ActionRequiredSection = ({ userId }: ActionRequiredSectionProps) => {
  const { t } = useTranslation();
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { trackAction } = useAnalytics({ component: "ActionRequiredSection" });

  useEffect(() => {
    if (!userId) return;
    
    const fetchActionRequiredRequests = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('service_requests')
          .select(`
            id,
            customer_name,
            customer_phone,
            created_at,
            scheduled_at,
            status,
            paid,
            services (
              id,
              name
            )
          `)
          .eq('user_id', userId)
          .in('status', ['pending', 'accepted', 'completed'])
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) {
          console.error("Error fetching action required requests:", error);
        } else {
          // Filter for:
          // - pending
          // - accepted (not paid or not completed)
          // - completed (not paid)
          const filteredRequests = (data || []).filter(request => {
            // Filter out expired requests
            const isRequestExpired = isExpired(request.scheduled_at);
            if (isRequestExpired) return false;
            if (request.status === 'pending') return true;
            if (request.status === 'accepted' && !request.paid) return true;
            if (request.status === 'completed' && !request.paid) return true;
            return false;
          });
          setPendingRequests(filteredRequests);
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchActionRequiredRequests();
  }, [userId]);

  const handleViewAllRequests = () => {
    trackAction("view_all_pending_requests_click");
    navigate("/dashboard/service-requests?view=pending");
  };

  const formatDate = (dateString: string) => {
    const date = parseISO(dateString);
    if (isToday(date)) {
      return t('navigation.today');
    } else if (isTomorrow(date)) {
      return t('navigation.tomorrow');
    }
    return format(date, "MMM d");
  };
  
  const isExpired = (scheduledAt: string | null) => {
    // If there's no scheduled date, the request is not expired
    if (!scheduledAt) return false;
    
    const scheduledDate = parseISO(scheduledAt);
    const now = new Date();
    return isBefore(scheduledDate, now);
  };

  // Only render the section if there are pending requests
  if (loading) {
    return null; // Still hide while loading
  }
  
  if (pendingRequests.length === 0) {
    return (
      <div className="bg-gray-50 rounded-none md:rounded-xl shadow-sm border-x-0 md:border border-gray-200 px-2 py-3 md:p-6 mb-4 md:mb-6">
        <div className="flex items-center">
          <Bell className="w-5 h-5 text-gray-400 mr-2" />
          <h3 className="text-base md:text-lg font-semibold text-gray-600">{t('dashboard.actionRequired')}</h3>
        </div>
        <p className="text-xs md:text-sm text-gray-500 mt-2">
          {t('dashboard.noPendingRequests')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-amber-50 rounded-none md:rounded-xl shadow-sm border-x-0 md:border border-amber-200 px-2 py-3 md:p-6 mb-4 md:mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          <Bell className="w-5 h-5 text-amber-500 mr-2" />
          <h3 className="text-base md:text-lg font-semibold text-amber-800">{t('dashboard.actionRequired')}</h3>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-amber-700 hover:text-amber-900 hover:bg-amber-100 text-xs md:text-sm"
          onClick={handleViewAllRequests}
        >
          {t('dashboard.viewAll')} <ArrowRight className="ml-1 h-3 w-3 md:h-4 md:w-4" />
        </Button>
      </div>
      
      <p className="text-xs md:text-sm text-amber-700 mb-3 text-left">
        {t('dashboard.youHave')} {pendingRequests.length} {pendingRequests.length === 1 ? t('dashboard.serviceRequestWaiting') : t('dashboard.serviceRequestsWaiting')}.
      </p>
      
      <div className="space-y-3">
        {pendingRequests.slice(0, 3).map((request) => (
          <div 
            key={request.id} 
            className="bg-white rounded-md p-3 border border-amber-100 shadow-sm hover:shadow-md transition-colors cursor-pointer"
            onClick={() => navigate(`/dashboard/service-requests/${request.id}`)}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="max-w-[75%]">
                <h4 className="text-sm font-semibold text-gray-900 truncate text-left">
                  {request.services?.name || "Service Request"}
                </h4>
              </div>
              <div className="ml-2">
                <Badge className={
                  request.status === 'pending' ? "bg-yellow-100 text-yellow-800 text-[10px]" :
                  request.status === 'accepted' ? "bg-blue-100 text-blue-800 text-[10px]" :
                  request.status === 'completed' ? "bg-green-100 text-green-800 text-[10px]" :
                  "bg-gray-100 text-gray-800 text-[10px]"
                }>
                  {t(`status.${request.status}`)}
                </Badge>
              </div>
            </div>

            <div className="mb-2">
              <div className="flex flex-col text-xs space-y-1">
                <div className="text-gray-600 text-left">
                  <span className="inline-block w-20">{t('navigation.requestedOn')}:</span> 
                  {formatDate(request.created_at)}
                </div>
                {request.scheduled_at && (
                  <div className="text-amber-600 text-left">
                    <span className="inline-block w-20">{t('navigation.serviceOn')}:</span> 
                    {formatDate(request.scheduled_at)}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-between items-center mt-3">
              <div className="flex items-center">
                <Clock className="w-3 h-3 text-amber-500 mr-1" />
                <span className="text-xs text-gray-600 text-left">{request.customer_name || "Anonymous"}</span>
              </div>
              {/* Action Buttons */}
              {request.status === 'pending' && (
                <Button 
                  size="sm" 
                  className="h-7 text-xs w-[100px] bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/dashboard/service-requests/${request.id}`);
                  }}
                >
                  {t('requestActions.respond')}
                </Button>
              )}
              {request.status === 'accepted' && !request.paid && (
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    className="h-7 text-xs w-[100px] bg-blue-500 hover:bg-blue-600 text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Mark Complete action (navigate to detail for now)
                      navigate(`/dashboard/service-requests/${request.id}`);
                    }}
                  >
                    {t('requestActions.markComplete')}
                  </Button>
                  <Button 
                    size="sm" 
                    className="h-7 text-xs w-[100px] bg-green-500 hover:bg-green-600 text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Mark Paid action (navigate to detail for now)
                      navigate(`/dashboard/service-requests/${request.id}`);
                    }}
                  >
                    {t('requestActions.markPaid')}
                  </Button>
                </div>
              )}
              {request.status === 'completed' && !request.paid && (
                <Button 
                  size="sm" 
                  className="h-7 text-xs w-[100px] bg-green-500 hover:bg-green-600 text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Mark Paid action (navigate to detail for now)
                    navigate(`/dashboard/service-requests/${request.id}`);
                  }}
                >
                  {t('requestActions.markPaid')}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}; 